import { z } from "zod";

/**
 * Version-skew guard during canary releases: the SPA sends its build ID and the
 * API rejects incompatible clients so the UI can reload.
 */
export const BUILD_ID_HEADER = "x-esi-build-id";

/** `YYYYMMDDTHHMMSSZ-<sha>`; only the UTC timestamp prefix orders builds. */
const ORDERABLE_BUILD_ID = /^(\d{8}T\d{6}Z)(?:-[0-9A-Za-z.-]+)?$/;

/**
 * A build's release version (`scripts/version.ts`): `2026.10.30`, `2026.10.30.1` (a second cut
 * that day), `2026.10.30+3.ab12cd3` (three commits after the tag) or `dev`. Checked when a bundle
 * inlines it, so a malformed `ESI_VERSION` pin fails the build instead of shipping.
 */
export const VERSION_FORMAT = /^(?:\d{4}\.\d{2}\.\d{2}(?:\.[1-9]\d*)?(?:\+\d+\.[0-9a-f]+)?|dev)$/;

export function assertVersionFormat(version: string): string {
  if (!VERSION_FORMAT.test(version)) {
    throw new Error(`version ${JSON.stringify(version)} is not a release version (VERSION_FORMAT)`);
  }
  return version;
}

export const CLIENT_OUTDATED = "client_outdated";

export const clientOutdatedResponseSchema = z.object({ code: z.literal(CLIENT_OUTDATED) });

export type ClientOutdatedResponse = z.infer<typeof clientOutdatedResponseSchema>;

/**
 * True only when both IDs are orderable and `buildId` is strictly older than `minimum`.
 * Unknown formats (`dev`, third-party clients) are never considered outdated.
 */
export function isBuildOlderThan(buildId: string, minimum: string): boolean {
  const build = ORDERABLE_BUILD_ID.exec(buildId)?.[1];
  const min = ORDERABLE_BUILD_ID.exec(minimum)?.[1];
  return build !== undefined && min !== undefined && build < min;
}
